
import Vue from 'vue';
import { generateUniqueId } from '@/utils';
import { deprecationMixin } from './../mixins/deprecationMixin';

/**
 * A display input field functions to guide a user as they input alphanumeric content during an experience.
 */
export default Vue.extend({
    name: 'IrisDisplayInputFieldVariable',
    inheritAttrs: false,
    model: {
        event: 'display-input-field-input',
    },
    mixins: [deprecationMixin],
    props: {
        /**
         * Sets the id (HTML global attribute) for the component. If an id is not provided, one will be generated automatically.
         */
        elementId: String,
        /**
         * Sets the actionable text
         */
        actiontext: {
            type: String,
            default: '',
        },
        /**
         * Sets the alignment of the input display, action text, error text, and helper text
         */
        align: {
            type: String,
            default: 'center',
            validator: (value: string) => {
                return ['left', 'center', 'right'].includes(value.toLowerCase());
            },
        },
        /**
         * Sets the error state.<br /><br />
         * It is suggested to set hasError to 'false' when event 'display-input-field-focus' is emitted.
         */
        hasError: {
            type: Boolean,
            default: false,
        },
        /**
         * Sets the error message
         */
        errortext: {
            type: String,
            default: '',
        },
        /**
         * Sets the helper text
         */
        helpertext: {
            type: String,
            default: '',
        },
        /**
         * (Deprecated: Use elementId instead!) The textfield id.
         */
        id: String,
        /**
         * Marks the input as either an optional or required field
         */
        isOptional: {
            type: Boolean,
            default: false,
        },
        /**
         * Sets the popup keyboard type on supported mobile devices
         */
        keyboardType: {
            type: String,
            default: 'text',
            validator: (value: string) => {
                return ['text', 'decimal', 'numeric', 'tel', 'search', 'email', 'url'].includes(value.toLowerCase());
            },
        },
        /**
         * Sets the minimum length of the input
         */
        minlength: {
            type: Number,
            default: 1,
            validator: (value: number) => {
                return value > 0 && value < 9;
            },
        },
        /**
         * Sets the maximum length of the input
         */
        maxlength: {
            type: Number,
            default: 8,
            validator: (value: number) => {
                return value > 0 && value < 9;
            },
        },
        /**
         * The input value
         */
        value: String,
        /**
         * An object containing the strings used in the select dropdown.<br /><br />
         * <strong>Keys are:</strong><br /><br />
         * errortext: Sets the text that displays when hasError is true.<br /><br />
         * helpertext: Sets the helperText for the input.<br /><br />
         * actiontext: Sets the actiontext that appears in the field.<br /><br />
         */
        strings: {
            type: Object,
            default: () => ({}),
        },
        /**
         * Sets the spellcheck global attribute for the HTML input.
         */
        allowBrowserSpellCheck: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            identifier_: this.elementId || this.id || generateUniqueId('irisv_display_input_field_variable') as string,
            internalError_: this.hasError,
        };
    },
    computed: {
        mergedStrings(): any {
            return {
                errortext: this.errortext,
                helpertext: this.helpertext,
                actiontext: this.actiontext,
                ...this.strings,
            };
        },
        actionTextIdentifier_(): string {
            return `action_text-${this.identifier_}`;
        },
        errorTextIdentifier_(): string {
            return `error_text-${this.identifier_}`;
        },
        helperTextIdentifier_(): string {
            return `helper_text-${this.identifier_}`;
        },
        showActionText(): boolean {
            return this.value && this.value.length > 0 ? false : true;
        },
    },
    methods: {
        onBlur(event: Event) {
            /**
             * Emitted when the input blurs.
             */
            this.$emit('display-input-field-blur', event);

            const display = this.$refs.display as HTMLElement;
            const displayItems = display.querySelectorAll('.irisv-display-input-field-variable__display-item') as NodeList;

            displayItems.forEach((item: Node) => {
                const html = item as HTMLElement;

                // if the item has no text then wipe out the inline style css variable
                if (html.innerText.length === 0) {
                    html.style.cssText = '';
                }
            });
        },
        onFocus(event: Event) {
            /**
             * Emitted when the input gets focus.
             */
            this.$emit('display-input-field-focus', event);
        },
        onInput(event: Event) {
            /**
             * Emitted when the input changes.
             */
            this.$emit('display-input-field-input', (event.target as HTMLInputElement).value);

            this.updateDisplay();
        },
        updateDisplay() {
            const display = this.$refs.display as HTMLElement;
            const inputString = this.value;
            let inputArray = [];

            if (inputString) {
                // create array of display items to display them one at a time
                inputArray = inputString.split('').map((item) => {
                    return `<span class="irisv-display-input-field-variable__display-item">${item}</span>`;
                });

                if (inputArray.length === 0) {
                    inputArray.push('<span class="irisv-display-input-field-variable__display-item"></span>');
                }

                display.innerHTML = inputArray.join('');
            } else {
                display.innerHTML = '<span class="irisv-display-input-field-variable__display-item"></span>';
            }
        },
        setDisplaySizes() {
            const display = this.$refs.display as HTMLElement;
            if (!display) { return; }

            const actionTextContainer = this.$refs.actionTextContainer as HTMLElement;
            if (!actionTextContainer) { return; }

            // Calculate a possible font size based on actual text length
            const actualTextLength = this.value ? this.value.length : 1;
            const maxAllowedFontSize = 60;
            const buffer = 0.84; // This number works best for adding a buffer in the possibleFontSize calculation. I tried others, but they would result in incorrect sizes.

            // Calculate a possible font size based on actual text length
            const possibleFontSize = Math.floor(display.offsetWidth / actualTextLength * buffer);
            const fontSizeNumber = Math.max(16, Math.min(possibleFontSize, maxAllowedFontSize));
            display.style.fontSize = fontSizeNumber + 'px';

            // Position the action text
            actionTextContainer.style.top = fontSizeNumber - 26 + 'px';
        },
    },
    watch: {
        hasError(newVal) {
            this.internalError_ = newVal;
        },
        value() {
            this.updateDisplay();
        },
    },
    mounted() {
        this.updateDisplay();
        this.setDisplaySizes();
        window.addEventListener('resize', this.setDisplaySizes);
    },
    updated() {
        this.updateDisplay();
        this.setDisplaySizes();
        window.addEventListener('resize', this.setDisplaySizes);
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.setDisplaySizes);
    },
});
